import Icon from "@/v2-ui/icon"
import Button from "@/v2-backoffice-client/components/button"
import { useLayerSourceUsageQuery } from "@/v2-backoffice-client/layer/create/layer.create.sourceUsage.query"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"

export type LayerCreateSourceUsageProps = {
  url: string
}

/**
 * The duplicate check: which layers already reference this source (any
 * sublayer of the same service). A new layer on a used source is fine when
 * it serves a DIFFERENT business case — the warning nudges the operator to
 * open the existing ones (their sheets stack on the breadcrumb) before
 * building the same thing twice.
 */
function LayerCreateSourceUsage(props: LayerCreateSourceUsageProps) {
  const { url } = props

  const opener = useAppViewSheetOpener()
  const { data, loading } = useLayerSourceUsageQuery({
    variables: { url },
  })

  const usages = data?.getLayerSourceUsage ?? []

  if (loading || usages.length === 0) return null

  return (
    <div className="flex shrink-0 flex-col gap-1.5 rounded-md border border-amber-500/50 bg-amber-500/10 px-2.5 py-2 text-xs">
      <p className="flex items-center gap-1.5 font-medium">
        <Icon src="triangle-warning" size={12} className="shrink-0" />
        {usages.length === 1
          ? "1 layer is already built on this source"
          : `${usages.length} layers are already built on this source`}
        <span className="font-normal text-muted-foreground">
          — make sure the new one serves a different business case.
        </span>
      </p>
      <div className="flex flex-wrap gap-1">
        {usages.map((usage) => (
          <Button
            key={usage.FeatureCode}
            variant="outline"
            size="xs"
            className="rounded-full font-normal"
            title={`${usage.FeatureCode} (${usage.LayerTypeCode}) — open its sheet`}
            data-feature-code="UI_BackofficeLayers_Create_SourceUsage"
            onClick={() => opener.open("layer", usage.FeatureCode)}
          >
            <span
              className={
                usage.IsEnabled
                  ? "mr-1 inline-block size-1.5 rounded-full bg-green-500"
                  : "mr-1 inline-block size-1.5 rounded-full bg-muted-foreground"
              }
            />
            {usage.Name}
          </Button>
        ))}
      </div>
    </div>
  )
}

export default LayerCreateSourceUsage
